import { formatCurrency } from '@/utils';
import type { StatementLine } from '@/services/reconciliation';

/**
 * The comparison maths behind the Commission Reconciliation tab.
 *
 * The tab answers one question: **did the insurance company pay us what the
 * calculation page says it owed us?** Two numbers per policy:
 *
 *   received   = `stated_amount`   — what the insurer's statement says it paid
 *   calculated = `expected_amount` — the matched CommissionCalculation's
 *                                    `total_commission`, i.e. the auto-approved
 *                                    commission shown on the Calculations tab
 *
 * and the difference the user reads is `received - calculated`:
 * **positive means the insurer paid MORE than we calculated (green), negative
 * means a shortfall (red).**
 *
 * The API's own `delta` field is the OTHER way round — `services.py` computes
 * `delta = calc.total_commission - line.stated_amount` (the amount the ledger
 * has to be adjusted BY, hence `delta > 0` posts a *debit*). Rendering that raw
 * number under a "positive is good" colour rule paints a shortfall green and an
 * overpayment red, which is exactly backwards. Everything below therefore
 * derives the user-facing difference from the two amounts and only falls back to
 * negating `delta` when `expected_amount` is missing (a matched calculation that
 * was later detached — `matched_calculation` is `on_delete=SET_NULL`).
 */

/** File types `POST /commissions/statement-lines/upload/` accepts. */
export const STATEMENT_UPLOAD_EXTENSIONS = [
  '.csv',
  '.txt',
  '.xlsx',
  '.xlsm',
  '.xls',
  '.xlsb',
] as const;

/** #436: statement layouts the upload can be told to read as. */
export const STATEMENT_FORMAT_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'digit_policy_xlsb', label: 'Digit policy-level statement (.xlsb)' },
  { value: 'intermediary_incentive_xlsx', label: 'Intermediary Incentive Report (.xlsx)' },
  { value: 'generic_two_column', label: 'Two columns: policy number and amount' },
] as const;

/**
 * `accept` for the statement file input. Mirrors `tabular.STATEMENT_EXTENSIONS`
 * on the backend (CSV + Excel + .xlsb). MIME types are listed alongside the extensions
 * because some browsers/OSes report a workbook only by MIME type.
 */
export const STATEMENT_UPLOAD_ACCEPT = [
  ...STATEMENT_UPLOAD_EXTENSIONS,
  'text/csv',
  'text/plain',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
].join(',');

export type ComparisonTone = 'positive' | 'negative' | 'neutral' | 'unknown';

/** Parse an API decimal string. Returns null for null/''/non-numeric. */
export function toAmount(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : null;
}

/**
 * The user-facing difference behind an API `delta`, i.e. `-delta`.
 *
 * Named once and used everywhere a `delta` is shown (the table's fallback, the
 * net figure, every row of a line's correction history), because getting the
 * orientation wrong paints a shortfall green.
 */
export function differenceFromDelta(delta: string | number | null | undefined): number | null {
  const value = toAmount(delta);
  return value === null ? null : -value;
}

/**
 * Difference for one statement line, oriented as `received - calculated`.
 * Returns null when there is nothing to compare (line never matched a
 * calculation), which the table renders as an em dash rather than as zero.
 */
export function lineDifference(
  line: Pick<StatementLine, 'stated_amount' | 'expected_amount' | 'delta'>,
): number | null {
  const received = toAmount(line.stated_amount);
  const calculated = toAmount(line.expected_amount);
  if (received !== null && calculated !== null) return received - calculated;
  return differenceFromDelta(line.delta);
}

/**
 * Net difference across every line in scope, same orientation as
 * `lineDifference`. `summary.total_delta` is `Sum(delta)`, so negate it.
 */
export function netDifference(
  summary: { total_delta: string | null } | undefined | null,
): number | null {
  return differenceFromDelta(summary?.total_delta);
}

/** Green when the insurer paid more, red when it paid less. */
export function differenceTone(difference: number | null): ComparisonTone {
  if (difference === null) return 'unknown';
  if (difference > 0) return 'positive';
  if (difference < 0) return 'negative';
  return 'neutral';
}

export const TONE_CLASS: Record<ComparisonTone, string> = {
  positive: 'text-green-600 dark:text-green-400',
  negative: 'text-red-600 dark:text-red-400',
  neutral: 'text-gray-500 dark:text-gray-400',
  unknown: 'text-gray-400 dark:text-gray-500',
};

/** "+₹1,200" / "-₹800" / "—". The sign is what the user is scanning for. */
export function formatSignedAmount(difference: number | null): string {
  if (difference === null) return '—';
  const body = formatCurrency(Math.abs(difference));
  if (difference > 0) return `+${body}`;
  if (difference < 0) return `-${body}`;
  return body;
}
